import { Box, Stack } from '@mui/material';
import type { ContractPageData } from '@vy/api-definition';
import type { CustomFieldDefinition, CustomFieldValueWithDefinition } from '@vy/custom-fields-types';
import { buildCurrencyLevel } from '@vy/services';
import { CONTRACT_TYPES, CURRENCIES } from '@vy/types';
import { conditionalWithDefault, isNotNullOrUndefined } from '@vy/utils';
import { FC } from 'react';
import { useContractCreditPoolsList } from '../../../../hooks/requests/use-contract-credit-pools-list';
import { ContractDetails } from './components/ContractDetails';
import { ParentAgreementContractDetails } from './components/ParentAgreementContractDetails';
import { ParentAgreementProductsSection } from './components/ParentAgreementProductsSection';
import { ContractBillingScheduleSection } from './containers/ContractBillingScheduleSection';
import { getContractDetailItems } from './utils/contract-utils';
import { ContractEstimations } from './components/ContractEstimations.tsx';
import { CreditEstimationCard } from './CreditEstimationCard.tsx';

type ContractContentProps = {
  contractPageData: ContractPageData;
  customFieldsWithDefinitions: CustomFieldValueWithDefinition[] | undefined;
  customFieldDefinitions: CustomFieldDefinition[] | undefined;
  isEditable: boolean;
  isNetSuiteSynced: boolean;
  hideContractQuickActions?: boolean;
  isContractHasPhases?: boolean;
};

export const ContractContent: FC<ContractContentProps> = ({
  contractPageData,
  customFieldsWithDefinitions,
  customFieldDefinitions,
  isEditable,
  isContractHasPhases = false,
}) => {
  const { contract } = contractPageData;
  const contractDetailsItems = getContractDetailItems(contract);

  const { data: creditPoolsData } = useContractCreditPoolsList(contract.id);
  const creditPools = creditPoolsData?.creditPools ?? [];

  const contractDisplayStartDate = conditionalWithDefault(contract.initialStartDate, contract.startDate, () =>
    isNotNullOrUndefined(contract.revisedAt),
  );

  const isParentAgreement = contract.contractType === CONTRACT_TYPES.ParentAgreement;
  const customerCurrency = contract.customer.settings?.currency ?? CURRENCIES.Usd;
  const currency = buildCurrencyLevel({
    contractCurrency: contract.currencyOverride,
    customerCurrency,
  });

  if (isParentAgreement) {
    return (
      <Stack
        direction="row"
        spacing={2}
        sx={{ height: '100%', overflowX: 'hidden', scrollbarGutter: 'stable' }}
      >
        <Stack spacing={2} sx={{ flex: 1, height: '100%', minWidth: 0 }}>
          <Box sx={{ flex: 1 }}>
            <ParentAgreementContractDetails contract={contract} />
          </Box>
          <Box sx={{ flex: 7, minWidth: 0 }}>
            <ParentAgreementProductsSection plan={contract.plan} currency={currency} />
          </Box>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ height: '100%', overflowX: 'hidden', scrollbarGutter: 'stable' }}
    >
      <Stack spacing={2} sx={{ flex: 1, height: '100%', minWidth: 0 }}>
        <Box sx={{ flex: 1 }}>
          <ContractDetails
            contractName={contract.plan.name}
            detailItems={contractDetailsItems}
            status={contract.status}
            plan={contract.plan}
            contract={contract}
          />
        </Box>
        <Box
          sx={{
            flex: 7,
            minWidth: 0,
            display: 'flex',
          }}
        >
          <ContractBillingScheduleSection
            contractPageData={contractPageData}
            customFieldsWithDefinitions={customFieldsWithDefinitions}
            customFieldDefinitions={customFieldDefinitions}
            startDate={contractDisplayStartDate}
            endDate={contract.endDate}
          />
        </Box>
      </Stack>
      <Stack
        sx={{
          minWidth: '283px',
          position: 'sticky',
          alignSelf: 'flex-start',
          height: 'max-content',
          top: 0,
          gap: 2,
        }}
      >
        <ContractEstimations
          contract={contract}
          isContractEditable={isEditable}
          isContractHasPhases={isContractHasPhases}
        />
        {creditPools.map((creditPool) => (
          <CreditEstimationCard
            key={creditPool.id}
            creditPool={creditPool}
            currency={customerCurrency}
          />
        ))}
      </Stack>
    </Stack>
  );
};
